/**
 * Pueue lifecycle for the local-CC `claude` process.
 *
 * One labelled pueue task per user, started at install (or on demand) and
 * managed via the `pueue` CLI. We assume `pueue` and `pueued` are installed
 * and the daemon is running — surface a friendly error otherwise.
 */

import { execFileSync, spawnSync, spawn } from 'node:child_process';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { connect } from 'node:net';

export const TASK_LABEL = 'synkro-local-cc';
export const TMUX_SESSION = 'synkro-local-cc';
export const SESSION_DIR = join(homedir(), '.synkro', 'cc_sessions');

export const TASK_LABEL_2 = 'synkro-local-cc-2';
export const TMUX_SESSION_2 = 'synkro-local-cc-2';
export const SESSION_DIR_2 = join(homedir(), '.synkro', 'cc_sessions_2');

export const TASK_LABEL_3 = 'synkro-local-cc-3';
export const TMUX_SESSION_3 = 'synkro-local-cc-3';
export const SESSION_DIR_3 = join(homedir(), '.synkro', 'cc_sessions_3');

export const TASK_LABEL_4 = 'synkro-local-cc-4';
export const TMUX_SESSION_4 = 'synkro-local-cc-4';
export const SESSION_DIR_4 = join(homedir(), '.synkro', 'cc_sessions_4');

export class PueueError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'PueueError';
  }
}

interface PueueStatusEntry {
  id: number;
  label?: string;
  status: string | { Running?: unknown; Done?: { result?: string } };
  command: string;
  path: string;
}

interface PueueStatus {
  tasks: Record<string, PueueStatusEntry>;
}

function pueueAvailable(): void {
  const r = spawnSync('pueue', ['--version'], { encoding: 'utf-8' });
  if (r.status !== 0) {
    throw new PueueError('pueue CLI not found on PATH. Install pueue (https://github.com/Nukesor/pueue) and start `pueued`.');
  }
}

function statusJson(): PueueStatus {
  pueueAvailable();
  const r = spawnSync('pueue', ['status', '--json'], { encoding: 'utf-8' });
  if (r.status !== 0) {
    throw new PueueError(`pueue status failed: ${r.stderr || r.stdout || 'unknown error'} — is pueued running?`);
  }
  try {
    return JSON.parse(r.stdout) as PueueStatus;
  } catch (err) {
    throw new PueueError(`pueue status returned non-JSON output: ${r.stdout.slice(0, 200)}`, err);
  }
}

export interface TaskInfo {
  id: number;
  label: string;
  status: string;
  command: string;
  cwd: string;
}

function statusName(s: PueueStatusEntry['status']): string {
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') {
    if ('Running' in s) return 'Running';
    if ('Done' in s) {
      const result = (s as { Done?: { result?: string | object } }).Done?.result;
      if (typeof result === 'string') return `Done (${result})`;
      if (result && typeof result === 'object') return `Done (${Object.keys(result)[0] ?? 'unknown'})`;
      return 'Done';
    }
    return Object.keys(s)[0] ?? 'unknown';
  }
  return 'unknown';
}

export interface ChannelIdentity {
  taskLabel: string;
  tmuxSession: string;
  sessionDir: string;
}

export const CHANNEL_PRIMARY: ChannelIdentity = { taskLabel: TASK_LABEL, tmuxSession: TMUX_SESSION, sessionDir: SESSION_DIR };
export const CHANNEL_SECONDARY: ChannelIdentity = { taskLabel: TASK_LABEL_2, tmuxSession: TMUX_SESSION_2, sessionDir: SESSION_DIR_2 };
export const CHANNEL_TERTIARY: ChannelIdentity = { taskLabel: TASK_LABEL_3, tmuxSession: TMUX_SESSION_3, sessionDir: SESSION_DIR_3 };
export const CHANNEL_QUATERNARY: ChannelIdentity = { taskLabel: TASK_LABEL_4, tmuxSession: TMUX_SESSION_4, sessionDir: SESSION_DIR_4 };
export const ALL_CHANNELS: readonly ChannelIdentity[] = [CHANNEL_PRIMARY, CHANNEL_SECONDARY, CHANNEL_TERTIARY, CHANNEL_QUATERNARY];

/** Find a synkro local-cc task by label. Returns null if absent. */
export function findTask(channel: ChannelIdentity = CHANNEL_PRIMARY): TaskInfo | null {
  const data = statusJson();
  for (const [id, t] of Object.entries(data.tasks)) {
    if (t.label === channel.taskLabel) {
      return {
        id: Number(id),
        label: t.label,
        status: statusName(t.status),
        command: t.command,
        cwd: t.path,
      };
    }
  }
  return null;
}

/** True if a synkro local-cc task is currently Running. */
export function isRunning(channel: ChannelIdentity = CHANNEL_PRIMARY): boolean {
  const t = findTask(channel);
  return t?.status === 'Running';
}

export interface StartOptions {
  /** Override the channel plugin script path. */
  pluginPath?: string;
  /** Working directory for the claude process. */
  cwd?: string;
  /** Which channel to start (default: primary). */
  channel?: ChannelIdentity;
}

/**
 * Add a fresh pueue task for the local-cc claude session. Removes any prior
 * task with the same label first (Done or otherwise) so subsequent calls
 * always replace cleanly.
 */
export function startTask(opts: StartOptions = {}): TaskInfo {
  const ch = opts.channel ?? CHANNEL_PRIMARY;
  const cwd = opts.cwd ?? ch.sessionDir;
  // Remove ALL tasks with this label (not just the first) to clear stale duplicates
  let existing = findTask(ch);
  while (existing) {
    if (existing.status === 'Running' || existing.status === 'Queued') {
      spawnSync('tmux', ['kill-session', '-t', `=${ch.tmuxSession}`], { encoding: 'utf-8' });
      spawnSync('pueue', ['kill', String(existing.id)], { encoding: 'utf-8' });
      for (let i = 0; i < 10; i++) {
        const check = findTask(ch);
        if (!check || check.id !== existing.id || (check.status !== 'Running' && check.status !== 'Queued')) break;
        spawnSync('sleep', ['0.5'], { encoding: 'utf-8' });
      }
    }
    spawnSync('pueue', ['remove', String(existing.id)], { encoding: 'utf-8' });
    existing = findTask(ch);
  }

  const runScript = join(cwd, 'run-claude.sh');
  const args = [
    'add',
    '--label', ch.taskLabel,
    '--working-directory', cwd,
    '--',
    'bash', runScript,
  ];

  const r = spawnSync('pueue', args, { encoding: 'utf-8' });
  if (r.status !== 0) {
    throw new PueueError(`pueue add failed: ${r.stderr || r.stdout}`);
  }
  const created = findTask(ch);
  if (!created) {
    throw new PueueError(`pueue add succeeded but no task with label ${ch.taskLabel} found`);
  }
  return created;
}

/** Stop and remove the synkro local-cc task. Also kills the tmux session
 *  that owns the underlying claude process. No-op if neither is present. */
export function stopTask(channel: ChannelIdentity = CHANNEL_PRIMARY): void {
  spawnSync('tmux', ['kill-session', '-t', `=${channel.tmuxSession}`], { encoding: 'utf-8' });
  // Remove ALL tasks with this label, waiting for each kill to land
  let t = findTask(channel);
  while (t) {
    if (t.status === 'Running' || t.status === 'Queued') {
      spawnSync('pueue', ['kill', String(t.id)], { encoding: 'utf-8' });
      // Wait for the task to leave Running state before removing
      for (let i = 0; i < 10; i++) {
        const check = findTask(channel);
        if (!check || check.id !== t.id || (check.status !== 'Running' && check.status !== 'Queued')) break;
        spawnSync('sleep', ['0.5'], { encoding: 'utf-8' });
      }
    }
    spawnSync('pueue', ['remove', String(t.id)], { encoding: 'utf-8' });
    t = findTask(channel);
  }
}

/** Print the last N log lines for the task. Returns the raw text. */
export function tailLogs(lines = 80, channel: ChannelIdentity = CHANNEL_PRIMARY): string {
  const t = findTask(channel);
  if (!t) return `(no ${channel.taskLabel} task)`;
  const r = spawnSync('pueue', ['log', '--lines', String(lines), String(t.id)], { encoding: 'utf-8' });
  return r.stdout || r.stderr || '(no output)';
}

/** Ensure the task is running; start it if not. Returns the task info. */
export function ensureRunning(opts: StartOptions = {}): TaskInfo {
  const ch = opts.channel ?? CHANNEL_PRIMARY;
  const t = findTask(ch);
  if (t && t.status === 'Running') return t;
  return startTask(opts);
}

/** Probe a TCP port — true if something accepts a connection within timeoutMs. */
function probePort(host: string, port: number, timeoutMs = 500): Promise<boolean> {
  return new Promise(resolve => {
    const sock = connect(port, host);
    const done = (ok: boolean) => {
      try { sock.destroy(); } catch { /* noop */ }
      resolve(ok);
    };
    sock.once('connect', () => done(true));
    sock.once('error', () => done(false));
    sock.setTimeout(timeoutMs, () => done(false));
  });
}

/** Dismiss startup prompts in the tmux session. Best-effort.
 *  Sends '1' (dev-channels accept) then Enter (other prompts). */
function tmuxDismissPrompts(tmuxSession: string = TMUX_SESSION): void {
  spawnSync('tmux', ['send-keys', '-t', tmuxSession, '1'], { encoding: 'utf-8' });
  spawnSync('tmux', ['send-keys', '-t', tmuxSession, 'Enter'], { encoding: 'utf-8' });
}

/**
 * Wait up to `timeoutMs` for the channel TCP listener to come up. Each tick
 * the probe fails, send an Enter into the tmux session — that walks claude
 * past any startup confirmation dialogs (workspace trust, dev-channels,
 * MCP consent). The loop self-terminates the moment the port binds, so
 * we don't keep spamming Enters once claude is at the live `❯` prompt.
 */
export async function waitForChannelReady(
  port: number,
  timeoutMs = 60_000,
  host = '127.0.0.1',
  tmuxSession: string = TMUX_SESSION,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (await probePort(host, port)) return true;
    tmuxDismissPrompts(tmuxSession);
    await new Promise(r => setTimeout(r, 1000));
  }
  return probePort(host, port);
}

function brewInstall(pkg: string): boolean {
  const brew = spawnSync('brew', ['--version'], { encoding: 'utf-8' });
  if (brew.status !== 0) return false;
  console.log(`  Installing ${pkg} via brew...`);
  const r = spawnSync('brew', ['install', pkg], { encoding: 'utf-8', stdio: 'inherit', timeout: 120_000 });
  return r.status === 0;
}

/** Ensure pueue is installed and pueued daemon is running. Auto-installs on macOS. */
export function assertPueueInstalled(): void {
  let r = spawnSync('pueue', ['--version'], { encoding: 'utf-8' });
  if (r.status !== 0) {
    if (process.platform === 'darwin' && brewInstall('pueue')) {
      r = spawnSync('pueue', ['--version'], { encoding: 'utf-8' });
      if (r.status !== 0) throw new PueueError('pueue install succeeded but binary not found on PATH.');
    } else {
      throw new PueueError('pueue not found. Install it: brew install pueue (macOS) or https://github.com/Nukesor/pueue');
    }
  }
  // Ensure pueued daemon is running
  const status = spawnSync('pueue', ['status', '--json'], { encoding: 'utf-8', timeout: 5_000 });
  if (status.status !== 0) {
    console.log('  Starting pueued daemon...');
    const child = spawn('pueued', ['-d'], { stdio: 'ignore', detached: true });
    child.unref();
    // Give the daemon a moment to bind its socket
    spawnSync('sleep', ['1']);
    const retry = spawnSync('pueue', ['status', '--json'], { encoding: 'utf-8', timeout: 5_000 });
    if (retry.status !== 0) {
      throw new PueueError('pueue daemon not reachable after starting pueued. Check `pueued` manually.');
    }
  }
  spawnSync('pueue', ['parallel', '2'], { encoding: 'utf-8' });
}

/** Ensure claude CLI is installed. */
export function assertClaudeInstalled(): void {
  const r = spawnSync('claude', ['--version'], { encoding: 'utf-8' });
  if (r.status !== 0) {
    throw new PueueError('claude CLI not found on PATH. Install Claude Code first: https://docs.claude.com/claude-code');
  }
}

/** Ensure tmux is installed. Auto-installs on macOS. */
export function assertTmuxInstalled(): void {
  let r = spawnSync('tmux', ['-V'], { encoding: 'utf-8' });
  if (r.status !== 0) {
    if (process.platform === 'darwin' && brewInstall('tmux')) {
      r = spawnSync('tmux', ['-V'], { encoding: 'utf-8' });
      if (r.status !== 0) throw new PueueError('tmux install succeeded but binary not found on PATH.');
    } else {
      throw new PueueError('tmux not found. Install it: brew install tmux (macOS) or apt install tmux (Linux)');
    }
  }
}

/** No-throw helper used by the install command for status messages. */
export function readVersion(bin: string): string | null {
  try {
    return execFileSync(bin, ['--version'], { encoding: 'utf-8', timeout: 3000 }).trim();
  } catch {
    return null;
  }
}
